import React, { ReactNode } from "react";
import { Table } from "react-bootstrap";
import styled from "styled-components";
import Branding from "../../config/Branding";

const StyledTable = styled(Table)`
    border-collapse: separate;
    border-spacing: 0;
    
    th, td {
        border: thin solid #282828;
        padding: 8px 12px;
        text-align: center;
    }

    td {
        vertical-align: middle;
    }

    tbody tr:nth-child(odd) {
        background-color: #1a1a2e;
    }

    tbody tr:nth-child(even) {
        background-color: #16213e;
    }

    tbody tr:hover {
        background-color: #1f3460;
    }
`;

const StickyHeader = styled.thead`
    position: sticky;
    top: 0;
    z-index: 10;
`;

const GroupHeaderRow = styled.tr`
    th {
        background: ${Branding.darkBackground};
        font-weight: 600;
        font-size: 0.9em;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
`;

const ColumnHeaderRow = styled.tr`
    th {
        background: ${Branding.superDarkBackground};
        font-weight: 500;
        font-size: 0.85em;
    }
`;

const TableContainer = styled.div`
    overflow-y: auto;
    max-height: 75vh;
`;

export type ColumnGroup = {
    name: string;
    columns: Column[];
};

export type Column = {
    key: string;
    header: string;
    render?: (value: any, row: Record<string, any>) => ReactNode;
};

type MetricsTableProps = {
    data: Record<string, any>[];
    columnGroups: ColumnGroup[];
    rowKeyField: string;
    onRowClick?: (row: Record<string, any>) => void;
    selectedRowKey?: string;
    emptyMessage?: string;
};

/**
 * MetricsTable component with grouped column headers
 * Supports sticky headers and column grouping with colspan
 */
const MetricsTable: React.FC<MetricsTableProps> = ({
    data,
    columnGroups,
    rowKeyField,
    onRowClick,
    selectedRowKey,
    emptyMessage = "No data available"
}) => {
    // Calculate total columns for empty message
    const totalColumns = columnGroups.reduce((sum, group) => sum + group.columns.length, 0);

    return (
        <TableContainer>
            <StyledTable bordered variant="dark">
                <StickyHeader>
                    {/* Group Headers Row */}
                    <GroupHeaderRow>
                        {columnGroups.map((group, index) => (
                            <th 
                                key={`group-${index}`} 
                                colSpan={group.columns.length}
                            >
                                {group.name}
                            </th>
                        ))}
                    </GroupHeaderRow>
                    {/* Column Headers Row */}
                    <ColumnHeaderRow>
                        {columnGroups.map(group => 
                            group.columns.map(column => (
                                <th key={column.key}>
                                    {column.header}
                                </th>
                            ))
                        )}
                    </ColumnHeaderRow>
                </StickyHeader>
                <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td colSpan={totalColumns} style={{ textAlign: "center", padding: "20px" }}>
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        data.map((row, index) => {
                            const rowKey = row[rowKeyField];
                            const isSelected = selectedRowKey === rowKey;
                            
                            return (
                                <tr
                                    key={rowKey}
                                    onClick={() => onRowClick?.(row)}
                                    style={{
                                        cursor: onRowClick ? "pointer" : "default",
                                        ...(isSelected && { background: Branding.greyBlue })
                                    }}
                                >
                                    {columnGroups.map(group =>
                                        group.columns.map(column => (
                                            <td key={`${rowKey}-${column.key}`}>
                                                {column.render 
                                                    ? column.render(row[column.key], row)
                                                    : row[column.key] ?? "-"
                                                }
                                            </td>
                                        ))
                                    )}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </StyledTable>
        </TableContainer>
    );
};

export default MetricsTable;
